import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box
} from '@mui/material'
import { UserSummary, CreateEscalationRequest } from '../types/entities'
import { EscalationService } from '../services/EscalationService'

interface EscalationDialogProps {
  open: boolean
  onClose: () => void
  ticketId: string
  agents: UserSummary[]
  onSuccess: () => void
}

export function EscalationDialog({
  open,
  onClose,
  ticketId,
  agents,
  onSuccess
}: EscalationDialogProps) {
  const [escalatedToUserId, setEscalatedToUserId] = useState('')
  const [escalationReason, setEscalationReason] = useState('')
  const [escalationNotes, setEscalationNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClose = () => {
    if (loading) return
    setEscalatedToUserId('')
    setEscalationReason('')
    setEscalationNotes('')
    setError(null)
    onClose()
  }

  const handleSubmit = async () => {
    if (!escalatedToUserId || !escalationReason) {
      setError('Please select an agent and provide a reason for escalation')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const request: CreateEscalationRequest = {
        ticketId,
        escalatedToUserId,
        escalationReason,
        escalationNotes: escalationNotes || undefined
      }

      await EscalationService.createEscalation(request)
      onSuccess()
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Failed to escalate ticket')
    } finally {
      setLoading(false)
    }
  }

  // Filter out customer roles from agents list
  const availableAgents = agents.filter(a => a.role !== 'Customer')

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Escalate Ticket</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth required>
            <InputLabel>Escalate To</InputLabel>
            <Select
              value={escalatedToUserId}
              onChange={(e) => setEscalatedToUserId(e.target.value)}
              label="Escalate To"
              disabled={loading}
            >
              {availableAgents.map((agent) => (
                <MenuItem key={agent.userId} value={agent.userId}>
                  {agent.fullName} ({agent.role}) - {agent.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Reason"
            value={escalationReason}
            onChange={(e) => setEscalationReason(e.target.value)}
            required
            fullWidth
            disabled={loading}
            helperText="Brief reason for escalation"
          />

          <TextField
            label="Additional Notes"
            value={escalationNotes}
            onChange={(e) => setEscalationNotes(e.target.value)}
            multiline
            rows={4}
            fullWidth
            disabled={loading}
            helperText="Optional additional context or instructions"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !escalatedToUserId || !escalationReason}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Escalating...' : 'Escalate Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
